import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Form, { FormMessage } from '/@/components/form';
import LocalePreferences from '/@/components/locale-preferences';
import SocialButton from '/@/components/social-button';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { withRouter } from '/@/hooks/with-router';
import { _ } from '/@/locale';
import { login, setPreferences, socialLogin } from '/@/store/session/actions';
import { getAppInstance, getSessionCountry, getSessionLanguage } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { getOS, getOSVersion, validateEmail } from '/@/utils';
import { messageForUser } from '/@/utils/errors';
import { decodeNextUrl, encodeNextUrl, reverse } from '/@/utils/router';
import Input from '/@/v3/components/forms/input';
import TermsAndConditions from '../../terms-and-conditions';
import styles from './styles.module.css';

/** Same condition as App `shouldHoldFieldviewModalAfterLogin`: stay on login until the FieldView modal is resolved. */
function shouldHoldFieldviewModalAfterLogin(appInstance, user) {
    return Boolean(user && appInstance?.enableFieldviewBlockingModal && !user.validated_type);
}

export const openPrivacyPolicy = () => {
    if (settings.app.privacyPolicy) {
        window.open(settings.app.privacyPolicy, '_blank');
    } else {
        enqueueSnackbar(_('Ha ocurrido un error al cargar la política de privacidad'), {
            variant: 'error',
        });
    }
};

/**
 * `Title` equivalents. The old component rendered all of these at 13px via CSS modules;
 * the theme marks variant font sizes `!important`, so `subtitle2` (14px) is the closest
 * reachable size. `Typography color='primary'` resolves to the per-app theme colour,
 * which is what `Title`'s `theme` prop was doing by hand.
 */
/**
 * Shared props for the text-button links. `Link component='button'` brings the button
 * reset, cursor and hover underline with it, and unlike a clickable `Typography` it is
 * focusable and responds to Enter/Space. `color` stays per call site.
 */
const linkProps = { component: 'button', type: 'button', variant: 'subtitle2', underline: 'hover' };

@withRouter
@connect(
    (state) => ({
        country: getSessionCountry(state),
        language: getSessionLanguage(state),
        appInstance: getAppInstance(state),
    }),
    (dispatch) => bindActionCreators({ setPreferences, login, socialLogin, trackEvent }, dispatch)
)
@autobind
export class LoginForm extends Form {
    static contextTypes = {
        history: PropTypes.object,
        login: PropTypes.func,
        socialLogin: PropTypes.func,
        trackEvent: PropTypes.func,
        location: PropTypes.object,
        onClick: PropTypes.func,
    };

    fields = {
        email: { initial: '' },
        password: { initial: '' },
    };

    validation = {
        email: (values) => validateEmail(values.email),
    };

    handleKeyDown(event) {
        event.stopPropagation();
        if (event.keyCode === 13) this.handleLogin();
    }

    handleSocialLogin(provider) {
        return async () => {
            try {
                console.log('handleSocialLogin: PRE');
                await this.props.socialLogin({
                    provider,
                    next: encodeNextUrl(this.props.location),
                    history: this.props.history,
                    source: 'login',
                    onSuccess: () => this.setState({ loading: false, success: '' }),
                });
                console.log('handleSocialLogin: POST');
                this.setState({
                    loading: true,
                    error: '',
                    success: _('Iniciando sesión...'),
                });
            } catch (error) {
                this.setState({
                    loading: false,
                    error: messageForUser(error, _('Ha ocurrido un error...')),
                    success: '',
                });
            }
        };
    }

    async handleLogin() {
        this.setState({ loading: true, error: '' });
        try {
            const session = await this.props.login({ ...this.state.values, onSuccess: this.onSuccessLogin });
            if (!shouldHoldFieldviewModalAfterLogin(this.props.appInstance, session?.user)) {
                this.context.history(decodeNextUrl(this.props.location));
            } else {
                this.setState({ loading: false });
            }
        } catch (error) {
            this.setState({
                loading: false,
                error: messageForUser(error, _('Ha ocurrido un error...')),
                success: '',
            });
        }
    }

    showErrorMessage() {
        return (
            <div className={styles.error}>
                <FormMessage style='error'>{this.state.error}</FormMessage>
            </div>
        );
    }

    showSuccessMessage() {
        return (
            <div className={styles.success}>
                <FormMessage style='success'>{this.state.success}</FormMessage>
            </div>
        );
    }

    renderSocial() {
        const isIOS = getOS() === 'iOS';
        const osVersion = Number.parseFloat(getOSVersion());

        const { socialLoginProviders } = settings.app;
        const enableGoogle = socialLoginProviders.includes('google') && !isIOS;
        const enableFacebook = socialLoginProviders.includes('facebook') && (!isIOS || (isIOS && osVersion < 11.3));
        const enableTwitter = socialLoginProviders.includes('twitter');
        const enableFieldView = socialLoginProviders.includes('fieldview');

        return (
            <div className={styles.socialContainer}>
                {enableFieldView && (
                    <div className={styles.linkContainer}>
                        <div className={styles.fieldview} onClick={this.handleSocialLogin('fieldview')} />
                    </div>
                )}
                {enableGoogle && (
                    <SocialButton
                        provider='google'
                        disabled={this.state.loading}
                        onClick={this.handleSocialLogin('google')}
                        circular
                    />
                )}
                {enableFacebook && (
                    <SocialButton
                        provider='facebook'
                        disabled={this.state.loading}
                        onClick={this.handleSocialLogin('facebook')}
                        circular
                    />
                )}
                {enableTwitter && (
                    <SocialButton
                        provider='twitter'
                        disabled={this.state.loading}
                        onClick={this.handleSocialLogin('twitter')}
                        circular
                    />
                )}
            </div>
        );
    }

    renderHeader() {
        return (
            <div className={styles.header}>
                <span id={styles.icon} onClick={this.props.goBack} className='material-icons'>
                    arrow_back
                </span>
                <LocalePreferences
                    openUp
                    appearance='row'
                    showLabels={false}
                    country={this.props.country.id}
                    language={this.props.language}
                    onChange={this.props.setPreferences}
                    showCountry
                    handleCountryChange={() => {}}
                    showLanguage={false}
                />
            </div>
        );
    }

    /**
     * With the FieldView blocking modal enabled, open self-registration is a dead end:
     * a non-validated user could sign up and then be blocked right after login. Point
     * them at the access-request flow instead.
     */
    renderAccountCta() {
        if (this.props.appInstance?.enableFieldviewBlockingModal) {
            return (
                <Grid item container justifyContent='center' direction='column' sx={{ width: '100%', minWidth: 0 }}>
                    <Typography variant='subtitle2' align='center' color='primary.mui.inactive' fontWeight={500}>
                        {_(
                            'Si sos parte del equipo comercial o de un Distribuidor autorizado por Bayer y aún no tenés cuenta o no podés acceder:'
                        )}
                    </Typography>
                    <Grid item container justifyContent='center'>
                        <Link
                            {...linkProps}
                            color='secondary'
                            onClick={() => this.props.history(reverse('RequestAccess'))}
                        >
                            {_('Solicitá acceso')}
                        </Link>
                    </Grid>
                </Grid>
            );
        }

        return (
            <Grid item container justifyContent='center' direction='row'>
                <Grid item>
                    <Typography variant='subtitle2' color='primary.mui.inactive'>
                        {_('¿Aún no tenes cuenta?')}
                    </Typography>
                </Grid>
                <Grid item sx={{ ml: 1 }}>
                    <Link {...linkProps} color='primary' onClick={this.props.onClick}>
                        {_('Registrate aquí')}
                    </Link>
                </Grid>
            </Grid>
        );
    }

    renderFooter() {
        return (
            <div className={styles.footer}>
                <Typography variant='subtitle2' color='primary.mui.inactive'>
                    {_('¿Quéres acceder al Catálogo?')}
                </Typography>
                <Link {...linkProps} color='primary' onClick={this.props.goToCatalog}>
                    {_('Ingresa aquí')}
                </Link>
            </div>
        );
    }

    render() {
        return (
            <div className={this.props.isMobile && styles.root}>
                {this.props.isMobile && this.renderHeader()}
                <div>
                    <div className={classNames(styles.logo, styles[`theme-${settings.app.theme}`])} />
                    {/* Definite width, because this column otherwise shrink-wraps to its
                        widest child -- which made the inputs resize whenever the CTA copy
                        changed (flag on vs off, or a longer translation). */}
                    <Box sx={{ width: { xs: '100%', md: 440 }, maxWidth: '100%', mx: 'auto' }}>
                        <div className={styles.form} onKeyDown={this.handleKeyDown}>
                            <Input
                                fullWidth
                                type='email'
                                appearance='outline'
                                placeholder={_('E-mail')}
                                size='md'
                                extraClass={styles.input}
                                {...this.getFieldProps('email')}
                                color={settings.app.theme}
                            />
                            <Input
                                color={settings.app.theme}
                                fullWidth
                                type='password'
                                appearance='outline'
                                placeholder={_('Contraseña')}
                                size='md'
                                {...this.getFieldProps('password')}
                                extraClass={styles.input}
                                iconPassword
                            />
                            <div className={styles.resetLink}>
                                {/* Spacing carried over from the retired `.text-button`
                                    class: 16px above and below. */}
                                <Link
                                    {...linkProps}
                                    color='secondary'
                                    onClick={() => this.props.handleForgotPassword()}
                                    sx={{ py: 2, mb: 2 }}
                                >
                                    {_('Olvidé mi contraseña')}
                                </Link>
                            </div>
                            <Grid container justifyContent='center'>
                                <Button
                                    variant='contained'
                                    disabled={this.state.loading || !this.validate()}
                                    onClick={this.handleLogin}
                                    color='secondary'
                                    sx={{ mb: 2, px: 4 }}
                                >
                                    <div className={styles.flexCenter}>
                                        {this.state.loading ? (
                                            <Fragment>
                                                {_('Cargando...')} <Spinner className={styles.spinner} />
                                            </Fragment>
                                        ) : (
                                            _('Ingresar')
                                        )}
                                    </div>
                                </Button>
                            </Grid>
                            {this.state.error && this.showErrorMessage()}
                            {this.state.success && this.showSuccessMessage()}
                        </div>
                        <Grid container justifyContent='center' direction='column'>
                            {this.renderAccountCta()}
                            <Grid item container justifyContent='center' sx={{ mt: 2 }}>
                                <Stack direction='row' spacing={2} justifyContent='center' textAlign='center'>
                                    <Link
                                        {...linkProps}
                                        color='primary'
                                        onClick={() => this.setState({ showTerms: true })}
                                    >
                                        {_('Términos y condiciones')}
                                    </Link>

                                    <Link {...linkProps} color='primary' onClick={openPrivacyPolicy}>
                                        {_('Políticas de seguridad')}
                                    </Link>
                                </Stack>
                            </Grid>
                        </Grid>
                        <div className={styles.social}>
                            <div>{this.renderSocial()}</div>
                        </div>
                    </Box>
                </div>
                {this.props.isMobile && this.renderFooter()}

                <TermsAndConditions
                    visible={this.state.showTerms}
                    onClose={() => this.setState({ showTerms: false })}
                    onAccept={() => this.setState({ showTerms: false })}
                />
            </div>
        );
    }
}
